/**
 * "Scrivi a piu' persone": quando parte ogni messaggio.
 *
 * Numero Standard (WAHA): tetto di 50 destinatari per invio e una pausa
 * casuale di 20-60 secondi fra un messaggio e l'altro. Un numero normale che
 * manda decine di messaggi identici a raffica viene segnalato e bloccato da
 * WhatsApp; la pausa variabile imita un invio fatto a mano.
 * Numero Ufficiale (Cloud): modelli approvati, Meta regge ben altri volumi;
 * 250 per invio (il primo scalino di Meta e' 250 conversazioni al giorno) e
 * pochi secondi fra un messaggio e l'altro.
 *
 * Di sera e di notte (21:00-08:00, ora italiana) non si parte: l'invio slitta
 * alle 8:00.
 *
 * Gli orari diventano `scheduled_at` delle righe di whatsapp_scheduled_messages;
 * il core le accoda rispettando l'orario (vedi flusia-core bulkScheduled.ts).
 *
 * Modulo puro, testato in __tests__/bulkSchedule.test.ts.
 */

export type BulkChannel = 'standard' | 'ufficiale';

export const BULK_LIMITS: Record<BulkChannel, { max: number; minGapSec: number; maxGapSec: number }> = {
  standard: { max: 50, minGapSec: 20, maxGapSec: 60 },
  ufficiale: { max: 250, minGapSec: 2, maxGapSec: 4 },
};

/** Margine prima del primo invio: il tempo di vedere il resoconto e annullare. */
export const START_DELAY_MS = 60_000;

const NIGHT_START_HOUR = 21;
const MORNING_HOUR = 8;
const TZ = 'Europe/Rome';

function italyHour(date: Date): number {
  const h = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, hour: '2-digit', hour12: false }).format(date);
  return parseInt(h, 10) % 24;
}

export function isNightInItaly(date: Date): boolean {
  const h = italyHour(date);
  return h >= NIGHT_START_HOUR || h < MORNING_HOUR;
}

/**
 * Primo orario utile: adesso piu' il margine, oppure le 8:00 se e' sera o notte.
 * Avanza a passi di un minuto (al massimo 11 ore): semplice e corretto anche
 * nei giorni del cambio d'ora.
 */
export function firstSendTime(now: Date): Date {
  return skipNight(new Date(now.getTime() + START_DELAY_MS));
}

/** Se l'orario cade di sera o di notte, lo sposta alle 8:00 del mattino dopo. */
function skipNight(date: Date): Date {
  let t = date;
  if (!isNightInItaly(t)) return t;
  for (let i = 0; i < 12 * 60 && isNightInItaly(t); i++) t = new Date(t.getTime() + 60_000);
  // Allinea al minuto pieno (le 8:00 precise).
  t.setUTCSeconds(0, 0);
  return t;
}

/** Orari di invio: il primo a `start`, poi pause casuali entro i limiti del canale. */
export function computeSendTimes(count: number, start: Date, channel: BulkChannel, rng: () => number = Math.random): Date[] {
  const { minGapSec, maxGapSec } = BULK_LIMITS[channel];
  const out: Date[] = [];
  let t = start.getTime();
  for (let i = 0; i < count; i++) {
    if (i > 0) {
      const r = Math.min(Math.max(rng(), 0), 1);
      t += Math.round((minGapSec + r * (maxGapSec - minGapSec)) * 1000);
      // Un invio lungo iniziato di sera non sconfina oltre le 21: il resto parte alle 8.
      t = skipNight(new Date(t)).getTime();
    }
    out.push(new Date(t));
  }
  return out;
}

/** Durata indicativa in minuti (pausa media), per il riepilogo. */
export function estimatedMinutes(count: number, channel: BulkChannel): number {
  if (count <= 1) return 0;
  const { minGapSec, maxGapSec } = BULK_LIMITS[channel];
  return Math.ceil(((count - 1) * (minGapSec + maxGapSec)) / 2 / 60);
}

/** Durata nel riepilogo: "Subito" con un solo destinatario (nessuna pausa), poi i minuti stimati. */
export function durationLabel(count: number, channel: BulkChannel): string {
  if (count <= 1) return 'Subito';
  const minuti = estimatedMinutes(count, channel);
  return minuti <= 1 ? 'Circa un minuto' : `Circa ${minuti} minuti`;
}

export function channelOf(serviceName: string | null | undefined): BulkChannel {
  return serviceName === 'whatsapp_cloud' || serviceName === 'whatsapp_cloud_coex' ? 'ufficiale' : 'standard';
}
